import { useCallback, useEffect, useState } from 'react';
import { Box, Button, InputAdornment, MenuItem, TextField, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useDispatch, useSelector } from 'react-redux';
import { CommonDatagrid } from '..';
import { OrderErrorsService } from '../../../api/services/orderErrorsService';
import {
  setOrderErrors,
  setPagination,
  setSelectedError,
} from '../../../store/Reducers/OrderErrorsSlice';
import { showNotification } from '../../../store/Reducers/NotificationSnackbarSlice';
import { IOrderError } from '../../../types/api/response/ordererrorsresponse';
import { IRootState } from '../../../types/store';
import { getOrderErrorsColumns } from './columns';
import { OrderErrorDetailDialog } from './OrderErrorDetailDialog';

const STAGES = [
  'ORDER_QUOTE',
  'ORDER_PERSIST',
  'CIN7_SALE_CREATE',
  'CIN7_SALE_ORDER',
  'CIN7_CREDIT_PAYMENT',
  'CIN7_FULFILMENT',
  'CIN7_VOID',
  'LOCUS_PUSH',
  'PAYMENT_CALLBACK',
  'NOTIFICATION',
];

const DEPENDENCIES = ['INTERNAL', 'CIN7', 'LOCUS', 'PAYMENT_GATEWAY', 'ONESIGNAL'];

export const OrderErrorsDatagrid = () => {
  const dispatch = useDispatch();
  const { errors, totalElements, pagination, selectedError } = useSelector(
    (state: IRootState) => state.orderErrors,
  );

  const [stage, setStage] = useState('');
  const [dependency, setDependency] = useState('');
  const [search, setSearch] = useState('');
  const [detailOpen, setDetailOpen] = useState(false);

  const columns = getOrderErrorsColumns();

  const fetchErrors = useCallback(
    async (
      page = 0,
      size = pagination.size,
      stageVal = stage,
      dependencyVal = dependency,
      searchVal = search,
    ) => {
      try {
        const res = await OrderErrorsService.searchOrderErrors({
          ...(stageVal ? { stage: stageVal } : {}),
          ...(dependencyVal ? { dependency: dependencyVal } : {}),
          ...(searchVal ? { search: searchVal } : {}),
          page,
          size,
        });
        dispatch(setOrderErrors({ content: res.content, totalElements: res.page.totalElements }));
        dispatch(setPagination({ page: res.page.number, size: res.page.size }));
      } catch {
        dispatch(showNotification({ message: 'Failed to fetch order errors.', severity: 'error' }));
      }
    },
    [dispatch, pagination.size, stage, dependency, search],
  );

  useEffect(() => {
    fetchErrors(0, pagination.size, '', '', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openDetail = (error: IOrderError) => {
    dispatch(setSelectedError(error));
    setDetailOpen(true);
  };

  const filterBar = (
    <Box display="flex" flexWrap="wrap" gap={2} alignItems="flex-end" mb={2}>
      <TextField
        select
        label="Stage"
        size="small"
        value={stage}
        onChange={(e) => setStage(e.target.value)}
        sx={{ minWidth: 200 }}
      >
        <MenuItem value="">All stages</MenuItem>
        {STAGES.map((s) => (
          <MenuItem key={s} value={s}>
            {s}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        select
        label="Dependency"
        size="small"
        value={dependency}
        onChange={(e) => setDependency(e.target.value)}
        sx={{ minWidth: 180 }}
      >
        <MenuItem value="">All dependencies</MenuItem>
        {DEPENDENCIES.map((d) => (
          <MenuItem key={d} value={d}>
            {d}
          </MenuItem>
        ))}
      </TextField>
      <TextField
        size="small"
        label="Search"
        placeholder="Search by order number, DS order number or phone..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ minWidth: 320 }}
        slotProps={{
          input: {
            endAdornment: (
              <InputAdornment position="end">
                <Tooltip
                  title="Search by order number (ORD-), DS order number (SO-), or customer phone number"
                  arrow
                >
                  <InfoOutlinedIcon
                    fontSize="small"
                    sx={{ color: 'text.secondary', cursor: 'help' }}
                  />
                </Tooltip>
              </InputAdornment>
            ),
          },
        }}
      />
      <Button
        variant="contained"
        onClick={() => fetchErrors(0, pagination.size, stage, dependency, search)}
        sx={{ fontSize: 12, fontWeight: 600, height: 40 }}
      >
        Apply Filters
      </Button>
      <Button
        variant="outlined"
        onClick={() => {
          setStage('');
          setDependency('');
          setSearch('');
          fetchErrors(0, pagination.size, '', '', '');
        }}
        sx={{ fontSize: 12, height: 40 }}
      >
        Clear
      </Button>
    </Box>
  );

  return (
    <>
      <CommonDatagrid
        header={filterBar}
        data={errors}
        columns={columns}
        rowId="id"
        serverPagination
        page={pagination.page}
        pageSize={pagination.size}
        rowCount={totalElements}
        onPageChange={(p) => fetchErrors(p, pagination.size)}
        onPageSizeChange={(s) => fetchErrors(0, s)}
        onRowClick={({ row }) => openDetail(row as IOrderError)}
        isCellEditable={() => false}
      />
      <OrderErrorDetailDialog
        error={selectedError}
        isOpen={detailOpen}
        onClose={() => {
          setDetailOpen(false);
          dispatch(setSelectedError(null));
        }}
      />
    </>
  );
};
